import { Feature, FeatureRenderOptions, MarkerUrls } from '../feature.model';
import { MapDrawingContext } from '../../models/map-drawing-context.model';
import { Document, FieldDataType, SystemFieldNames } from 'src/app/modules/core/store/models/document.model';
import { GoogleMapFeatures } from '../../models/layer-map-features.model';
import { FeatureRenderingUtilityService } from 'src/app/modules/cop/drawing/features/feature-rendering-utility.service';
import { Layer } from 'src/app/modules/cop/store/cop/models/layer.model';
import { Dataset, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import { cyrb53 } from 'src/app/modules/core/services/hasher';
import { LayerColors } from '../../models/layer-colors.model';
import { GeometryTypes } from '../../models/layer-renderer.model';
import { DatasetZIndexValues } from 'src/app/modules/cop/drawing/models/dataset-zIndex.model';
import { FeatureEvent } from 'src/app/modules/cop/drawing/features/feature.model';
import { MapLabelService } from '../../label.service';
import { v4 as uuidv4 } from 'uuid';
import { MapLayerQueryBuildService } from '../../../services/data/map-layer-query-build.service';
import numeral from 'numeral';
import { GeoJsonUtilityService } from '../../geo-json-utility.service';
import { MapLayerClusterService } from '../../cluster.service';
import { PopupService } from '../../popups/popup.service';

export const SELECTED_Z_INDEX = 10000;

export class EarthquakeFeature implements Feature {
  private googleMapFeatures: GoogleMapFeatures;
  private documentHash: number = 0;
  private geometryHash: number = 0;
  private isVisible: boolean = false;
  private isSelected: boolean = false;
  private markerUrls: MarkerUrls = { normal: '', selected: '' };
  private zIndex: number = 0;
  private mouseoutTimers = {};
  private layerUId: string;
  private datasetUId: string;
  private documentUId: string;
  private layerColors: LayerColors;
  private label?: string;
  private minLabelZoomLevel: number;
  private layerHasLabels: boolean;
  private labelService: MapLabelService;
  private clusterService: MapLayerClusterService;
  private extraGeoJsonValue: string;
  private timerId: string | number | NodeJS.Timeout;
  private popup: PopupService;
  private canShowPopup: boolean;
  private popupOffset: number;

  constructor() {}

  render(context: MapDrawingContext, options: FeatureRenderOptions): void {
    const { layer, document } = options;

    this.googleMapFeatures = context.mapFeatures[layer.uId][document.uid];

    const dataset = context.mapState.datasetsSet[layer.datasetUId];

    this.layerUId = layer.uId;
    this.datasetUId = dataset.uId;
    this.documentUId = document.uid;

    this.isSelected = !!context.mapState.selectedDocuments[document.uid];
    this.isVisible = context.isDocumentVisible(layer, document);

    this.label = MapLayerQueryBuildService.getDatasetLabelFieldDisplay(document, context.labelFields);
    this.labelService = context.labelService;
    this.clusterService = context.clusterService;
    this.popup = context.popup;
    this.canShowPopup = context.popup?.isEnabledForLayer(layer);
    this.minLabelZoomLevel = this.labelService.getLayerLabelMinZoom(layer);
    this.layerHasLabels = layer.displayOptions?.labelsVisible === true;

    const backgroundColor = this.getBackgroundColor(document);
    this.layerColors = this.getLayerColors(backgroundColor);

    const magnitude = this.getMagnitude(document);

    this.markerUrls.normal = `/api/v1/meta/markers/earthquake-magnitude-${magnitude}/${backgroundColor}/0/0/normal/earthquake.png`;
    this.markerUrls.selected = `/api/v1/meta/markers/earthquake-magnitude-${magnitude}/${backgroundColor}/0/0/selected/earthquake.png`;
    this.popupOffset = this.getPopupOffset(magnitude);
    this.zIndex = this.isSelected ? SELECTED_Z_INDEX : DatasetZIndexValues[dataset.type][dataset.subtype];
    const extraGeoField = 'shake_map_geometry';

    const showExtraGeo = context.mapState.geoFieldsForOptionalDisplay?.some(
      f => f.layerUId == layer.uId && f.fieldNames?.includes(extraGeoField) && (!!!f.stepUId || document.workflow_step_uid === f.stepUId)
    );

    // build and check the document hash...if the values have not changed then
    // bail out on the rendering process.
    const newHash = this.buildDocumentHash(dataset, layer, document, this.isSelected, this.label, showExtraGeo);
    if (this.documentHash === newHash) {
      return;
    }
    this.documentHash = newHash;

    // check to see if the geometry has changed. If so then we need to remove all existing google map object
    // and redraw. If not then update the colors and icons in the case the workflow has been updated.
    const geoFieldName = FeatureRenderingUtilityService.getGeometryFieldSystemName(dataset, layer);
    const geoJsonValue = document[geoFieldName];
    const shakeMapValue = showExtraGeo ? document[extraGeoField] : null;

    const newGeoHash = this.buildGeometryHash(geoJsonValue ?? '');
    if (newGeoHash !== this.geometryHash) {
      this.destroy();

      // get the geometry features from the standard geo field in the document
      // and then render each to the map
      this.addGoogleMapObjects(context, layer, dataset, document, geoFieldName);

      this.geometryHash = newGeoHash;
    } else {
      // this is an update where the geometry has not changed. just update the geometry colors and marker icons
      this.updateGoogleMapObjects(context, layer, dataset, document);
    }
    if (this.extraGeoJsonValue !== shakeMapValue) {
      if (!!this.extraGeoJsonValue && !!!shakeMapValue) {
        this.removeExtraGoogleMapObjects(context, layer, dataset, extraGeoField);
      }
      if (!!!this.extraGeoJsonValue && !!shakeMapValue) {
        this.addExtraGoogleMapObjects(context, layer, dataset, document, extraGeoField, shakeMapValue);
      }
      this.extraGeoJsonValue = shakeMapValue;
    }
  }

  setVisible() {
    this.isVisible = true;
    for (let marker of this.googleMapFeatures.markers) {
      marker.setVisible(true);
      this.clusterService.registerMarkerAdd(this.layerUId, marker);
    }
    if (this.layerHasLabels === true) {
      this.showLabel();
    }

    if (this.layerHasLabels === true) {
      this.showLabel();
    }
    for (let poly of this.googleMapFeatures.polygons) {
      poly.setVisible(true);
    }
    for (let line of this.googleMapFeatures.polylines) {
      line.setVisible(true);
    }
    for (let circ of this.googleMapFeatures.circles) {
      circ.setVisible(true);
    }
    for (let rect of this.googleMapFeatures.rectangles) {
      rect.setVisible(true);
    }
  }

  setHidden() {
    this.isVisible = false;
    for (let marker of this.googleMapFeatures.markers) {
      marker.setVisible(false);
      const labelOverlay = this.labelService.getMarkerLabel(this.layerUId, marker);
      if (labelOverlay) {
        labelOverlay.setVisible(false);
      }
      this.clusterService.registerMarkerRemove(this.layerUId, marker);
    }
    for (let poly of this.googleMapFeatures.polygons) {
      poly.setVisible(false);
    }
    for (let line of this.googleMapFeatures.polylines) {
      line.setVisible(false);
    }
    for (let circ of this.googleMapFeatures.circles) {
      circ.setVisible(false);
    }
    for (let rect of this.googleMapFeatures.rectangles) {
      rect.setVisible(false);
    }

    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  setSelected() {
    this.isSelected = true;
    this.setMarkerSelected();
  }

  setUnselected() {
    this.isSelected = false;
    this.setMarkerNormal();
  }

  showLabel(): void {
    this.layerHasLabels = true;
    if (!!!this.label || this.googleMapFeatures.markers.length === 0) {
      return;
    }

    for (let marker of this.googleMapFeatures.markers) {
      let labelOverlay = this.labelService.getMarkerLabel(this.layerUId, marker);

      if (!labelOverlay) {
        labelOverlay = this.labelService.createLabel(
          marker,
          this.label,
          this.layerUId,
          this.documentUId,
          {
            left: null,
            top: -80
          },
          'map-marker-label'
        );
        this.labelService.addMarkerLabel(this.layerUId, this.minLabelZoomLevel, labelOverlay);
      } else {
        this.labelService.checkMarkerLabelDisplay(this.layerUId, this.minLabelZoomLevel, labelOverlay);
      }
    }
  }

  hideLabel(): void {
    this.layerHasLabels = false;
    for (let marker of this.googleMapFeatures.markers) {
      this.labelService.deleteMarkerLabel(this.layerUId, marker);
    }
  }

  private updateLabel(layer: Layer, marker: any): void {
    const hasLabel = !!this.label && layer.displayOptions?.labelsVisible === true;
    this.layerHasLabels = layer.displayOptions?.labelsVisible === true;

    let labelOverlay = this.labelService.getMarkerLabel(this.layerUId, marker);
    if (hasLabel) {
      if (labelOverlay) {
        labelOverlay.setLabel(this.label);
        this.labelService.checkMarkerLabelDisplay(this.layerUId, this.minLabelZoomLevel, labelOverlay);
      } else {
        this.showLabel();
      }
    } else {
      this.hideLabel();
    }
  }

  destroy(): void {
    for (let circle of this.googleMapFeatures.circles) {
      circle.unbindAll();
      circle.setMap(null);
    }
    this.googleMapFeatures.circles = [];

    for (let marker of this.googleMapFeatures.markers) {
      this.labelService.deleteMarkerLabel(this.layerUId, marker);
      this.clusterService.registerMarkerRemove(this.layerUId, marker);
      marker.unbindAll();
      marker.setMap(null);
    }
    this.googleMapFeatures.markers = [];

    for (let polygon of this.googleMapFeatures.polygons) {
      polygon.unbindAll();
      polygon.setMap(null);
    }
    this.googleMapFeatures.polygons = [];

    for (let polyline of this.googleMapFeatures.polylines) {
      polyline.unbindAll();
      polyline.setMap(null);
    }
    this.googleMapFeatures.polylines = [];

    for (let rectangle of this.googleMapFeatures.rectangles) {
      rectangle.unbindAll();
      rectangle.setMap(null);
    }
    this.googleMapFeatures.rectangles = [];
  }

  addGoogleMapObjects(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geoField: string) {
    const geoFeatures = FeatureRenderingUtilityService.getGeometryFeaturesForDisplay(dataset, layer, document);
    if (!geoFeatures) {
      return;
    }

    for (let geo of geoFeatures) {
      this.addGoogleMapObject(context, layer, dataset, document, geo, geoField);
    }
  }

  addExtraGoogleMapObjects(
    context: MapDrawingContext,
    layer: Layer,
    dataset: Dataset,
    document: Document,
    geoField: string,
    extraGeometryJson: string
  ) {
    let extraMapGeometry = JSON.parse(extraGeometryJson);
    const extraMapFeatures = GeoJsonUtilityService.getNormalizedGeometriesOrFeaturesFromGeoJson(extraMapGeometry);

    for (let geo of extraMapFeatures) {
      this.addGoogleMapObject(context, layer, dataset, document, geo, geoField);
    }
  }

  removeExtraGoogleMapObjects(context: MapDrawingContext, layer: Layer, dataset: Dataset, geoField: string) {
    for (let i = this.googleMapFeatures.circles.length - 1; i >= 0; i--) {
      if (this.googleMapFeatures.circles[i]['__data'].geoField === geoField) {
        this.googleMapFeatures.circles[i].unbindAll();
        this.googleMapFeatures.circles[i].setMap(null);
        this.googleMapFeatures.circles.splice(i, 1);
      }
    }

    for (let i = this.googleMapFeatures.markers.length - 1; i >= 0; i--) {
      if (this.googleMapFeatures.markers[i]['__data'].geoField === geoField) {
        this.labelService.deleteMarkerLabel(this.layerUId, this.googleMapFeatures.markers[i]);
        this.clusterService.registerMarkerRemove(this.layerUId, this.googleMapFeatures.markers[i]);
        this.googleMapFeatures.markers[i].unbindAll();
        this.googleMapFeatures.markers[i].setMap(null);
        this.googleMapFeatures.markers.splice(i, 1);
      }
    }

    for (let i = this.googleMapFeatures.polygons.length - 1; i >= 0; i--) {
      if (this.googleMapFeatures.polygons[i]['__data'].geoField === geoField) {
        this.googleMapFeatures.polygons[i].unbindAll();
        this.googleMapFeatures.polygons[i].setMap(null);
        this.googleMapFeatures.polygons.splice(i, 1);
      }
    }

    for (let i = this.googleMapFeatures.polylines.length - 1; i >= 0; i--) {
      if (this.googleMapFeatures.polylines[i]['__data'].geoField === geoField) {
        this.googleMapFeatures.polylines[i].unbindAll();
        this.googleMapFeatures.polylines[i].setMap(null);
        this.googleMapFeatures.polylines.splice(i, 1);
      }
    }

    for (let i = this.googleMapFeatures.rectangles.length - 1; i >= 0; i--) {
      if (this.googleMapFeatures.rectangles[i]['__data'].geoField === geoField) {
        this.googleMapFeatures.rectangles[i].unbindAll();
        this.googleMapFeatures.rectangles[i].setMap(null);
        this.googleMapFeatures.rectangles.splice(i, 1);
      }
    }
  }

  addGoogleMapObject(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any, geoField: string) {
    if (geometry.type === GeometryTypes.point) {
      this.addPoint(context, layer, dataset, document, geometry, geoField);
    } else if (geometry.type === GeometryTypes.feature) {
      this.addFeature(context, layer, dataset, document, geometry, geoField);
    }
  }

  addPoint(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any, geoField: string) {
    // account for features that have valid geojson but has empty or invalid coordinates array
    const [lng = null, lat = null] = geometry?.coordinates ?? [];
    
    if (typeof lat !== 'number' || typeof lng !== 'number') {
      return;
    }

    const iconsSizes = [21, 25, 29, 35, 45, 51, 57];

    const magnitude = this.getMagnitude(document);
    const iconsSize = iconsSizes[parseInt(magnitude) - 1];
    const anchor = new google.maps.Point(iconsSize / 2, iconsSize / 2);

    const marker = new google.maps.Marker({
      position: { lat, lng },
      icon: {
        url: !this.isSelected ? this.markerUrls.normal : this.markerUrls.selected,
        anchor
      },
      visible: this.isVisible,
      zIndex: this.zIndex ?? 3000,
      map: context.googleMap
    });

    marker['__data'] = {
      id: uuidv4(),
      layerUId: layer.uId,
      datasetUId: dataset.uId,
      documentUId: document.uid,
      iconsSize: iconsSize,
      geoField
    };

    const mapEventService = context.mapEvents;

    marker.addListener('mouseover', event => {
      this.onMarkerMouseOver({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.open({
          position: marker.getPosition(),
          offsetY: this.popupOffset,
          layer,
          dataset,
          document,
          offsetBelow: event.domEvent.target.clientHeight
        });
      }
    });

    marker.addListener('mouseout', () => {
      this.onMarkerMouseOut({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    marker.addListener('click', event => {
      mapEventService.onFeatureClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    marker.addListener('dblclick', event => {
      mapEventService.onFeatureDoubleClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    this.googleMapFeatures.markers.push(marker);
    this.clusterService.registerMarkerAdd(this.layerUId, marker);

    if (layer.displayOptions?.labelsVisible === true) {
      this.showLabel();
    } else {
      this.hideLabel();
    }
  }

  addPolygon(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any, geoField: string) {
    const coords = FeatureRenderingUtilityService.normalizePolygonCoordinates(geometry.coordinates);

    const polygon = new google.maps.Polygon({
      paths: coords,
      strokeColor: this.layerColors.borderColor,
      fillColor: this.layerColors.fillColor,
      strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
      strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0,
      fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45,
      map: context.googleMap,
      visible: this.isVisible,
      zIndex: this.zIndex ?? 3000
    });

    polygon['__data'] = {
      layerUId: layer.uId,
      datasetUId: dataset.uId,
      documentUId: document.uid,
      geoField
    };

    const mapEventService = context.mapEvents;

    this.googleMapFeatures.polygons.push(polygon);

    polygon.addListener('click', event => {
      mapEventService.onFeatureClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polygon
      });
    });

    polygon.addListener('dblclick', event => {
      mapEventService.onFeatureDoubleClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polygon
      });
    });
  }

  addPolyline(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any, geoField: string) {
    // account for features that have valid geojson but has empty or invalid coordinates array
    const isValidGeometry = geometry?.coordinates?.every(c => !!c.length && c.every(x => typeof x === 'number')) ?? false;

    if (!isValidGeometry) {
      return;
    }

    const polyline = new google.maps.Polyline({
      path: geometry.coordinates?.map(c => new google.maps.LatLng(c[1], c[0])),
      strokeColor: this.layerColors.borderColor,
      strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 2,
      map: context.googleMap,
      visible: this.isVisible,
      zIndex: this.zIndex ?? 3000
    });

    polyline['__data'] = {
      layerUId: layer.uId,
      datasetUId: dataset.uId,
      documentUId: document.uid,
      geoField
    };

    const mapEventService = context.mapEvents;

    this.googleMapFeatures.polylines.push(polyline);

    polyline.addListener('click', event => {
      mapEventService.onFeatureClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polyline
      });
    });

    polyline.addListener('dblclick', event => {
      mapEventService.onFeatureDoubleClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polyline
      });
    });
  }

  addMultilinePolyline(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any, geoField: string) {
    const polyline = new google.maps.Polyline({
      path: geometry.map(c => new google.maps.LatLng(c[1], c[0])),
      strokeColor: this.layerColors.borderColor,
      strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 2,
      map: context.googleMap,
      visible: this.isVisible,
      zIndex: this.zIndex ?? 3000
    });

    polyline['__data'] = {
      layerUId: layer.uId,
      datasetUId: dataset.uId,
      documentUId: document.uid,
      geoField
    };

    const mapEventService = context.mapEvents;

    this.googleMapFeatures.polylines.push(polyline);

    polyline.addListener('click', event => {
      mapEventService.onFeatureClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polyline
      });
    });

    polyline.addListener('dblclick', event => {
      mapEventService.onFeatureDoubleClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polyline
      });
    });
  }

  addFeature(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any, geoField: string): void {
    if (geometry.geometry.type === GeometryTypes.point) {
      this.addPoint(context, layer, dataset, document, geometry.geometry, geoField);
    } else if (geometry.geometry.type === GeometryTypes.polygon || geometry.geometry.type === GeometryTypes.multiPolygon) {
      this.addPolygon(context, layer, dataset, document, geometry.geometry, geoField);
    } else if (geometry.geometry.type === GeometryTypes.lineString) {
      this.addPolyline(context, layer, dataset, document, geometry.geometry, geoField);
    } else if (geometry.geometry.type === GeometryTypes.multiLineString) {
      for (let lineString of geometry.geometry.coordinates) {
        this.addMultilinePolyline(context, layer, dataset, document, lineString, geoField);
      }
    }
  }

  updateGoogleMapObjects(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document): void {
    for (let marker of this.googleMapFeatures.markers) {
      const icon = marker.getIcon() as google.maps.Icon;
      icon.url = this.isSelected ? this.markerUrls.selected : this.markerUrls.normal;
      marker.setIcon(icon);
    }

    if (this.googleMapFeatures.markers.length) {
      this.updateLabel(layer, this.googleMapFeatures.markers[0]);
    }

    for (let polygon of this.googleMapFeatures.polygons) {
      polygon.setValues({
        strokeColor: this.layerColors.borderColor,
        fillColor: this.layerColors.fillColor,
        fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45,
        strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
        strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0
      });
    }

    for (let circle of this.googleMapFeatures.circles) {
      circle.setValues({
        strokeColor: this.layerColors.borderColor,
        fillColor: this.layerColors.fillColor,
        fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45,
        strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
        strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0
      });
    }

    for (let polyline of this.googleMapFeatures.polylines) {
      polyline.setValues({
        strokeColor: this.layerColors.borderColor,
        fillColor: this.layerColors.fillColor,
        strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
        strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0,
        fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45
      });
    }

    for (let rectangle of this.googleMapFeatures.rectangles) {
      rectangle.setValues({
        strokeColor: this.layerColors.borderColor,
        fillColor: this.layerColors.fillColor,
        strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
        strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0,
        fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45
      });
    }
  }

  buildDocumentHash(dataset: Dataset, layer: Layer, document: Document, isSelected: boolean, label: string, showExtraGeo: boolean): number {
    const geometryFieldName = FeatureRenderingUtilityService.getGeometryFieldSystemName(dataset, layer);

    const geometry = document[geometryFieldName] ?? '';
    const extraGeo = document['shake_map_geometry'] ?? '';
    const workflowStepUId = document.workflow_step_uid;
    const fillColor = document.fill_color ?? '';
    const borderColor = document.border_color ?? '';

    const stringToHash = [
      geometry,
      extraGeo,
      showExtraGeo.toString(),
      workflowStepUId,
      fillColor,
      borderColor,
      isSelected,
      label ?? ''
    ].join();

    return cyrb53(stringToHash);
  }

  buildGeometryHash(geometry: string): number {
    return cyrb53(geometry);
  }

  onMarkerMouseOver(e: FeatureEvent): void {
    if (this.mouseoutTimers[e.documentUId]) {
      clearTimeout(this.mouseoutTimers[e.documentUId]);
      this.mouseoutTimers[e.documentUId] = null;
    }

    this.setMarkerSelected();
    this.startHoverTimer();
  }

  onMarkerMouseOut(e: FeatureEvent): void {
    const that = this;
    const timeout = setTimeout(() => {
      if (!that.isSelected) {
        that.setMarkerNormal();
        clearTimeout(that.timerId);
        that.timerId = null;
      }
    }, 100);
    this.mouseoutTimers[e.documentUId] = timeout;
  }

  setMarkerSelected(): void {
    for (let marker of this.googleMapFeatures.markers) {
      const origZIndex = marker.getZIndex();
      marker['__data'].zIndex = origZIndex;

      const anchor = new google.maps.Point(marker['__data'].iconsSize / 2, marker['__data'].iconsSize / 2);

      marker.setZIndex(SELECTED_Z_INDEX);
      marker.setIcon({
        url: this.markerUrls.selected,
        anchor
      });
    }
  }

  setMarkerNormal(): void {
    for (let marker of this.googleMapFeatures.markers) {
      const zIndex = marker['__data'].zIndex;
      marker.setZIndex(zIndex);

      const anchor = new google.maps.Point(marker['__data'].iconsSize / 2, marker['__data'].iconsSize / 2);

      marker.setIcon({
        url: this.markerUrls.normal,
        anchor
      });
    }
  }

  getMagnitude(document: Document): string {
    if (!document.magnitude) {
      return '1';
    }

    var magnitudeWholeNumber = numeral(document.magnitude).format('0[.]');
    return magnitudeWholeNumber >= 7 ? '7' : magnitudeWholeNumber;
  }

  getBackgroundColor(document: Document): string {
    var startDate = new Date(document.start_date).getTime();
    var currentDateTime = new Date().getTime();
    var timeSinceMilliseconds = currentDateTime - startDate;
    var timeSinceDays = timeSinceMilliseconds / 86400000;

    if (timeSinceDays < 1) {
      return 'E03131';
    }

    if (timeSinceDays < 7) {
      return 'FD7E14';
    }

    if (timeSinceDays < 90) {
      return 'FCC419';
    }

    return 'D6D5DD';
  }

  getLayerColors(backgroundColor: string): LayerColors {
    var color = `#${backgroundColor}`;

    return {
      fillColor: color,
      borderColor: color
    };
  }

  startHoverTimer() {
    if (!!this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }

    const that = this;
    this.timerId = setTimeout(() => {
      if (!that.isSelected) {
        that.setMarkerNormal();
      }
    }, 3000);
  }

  getPopupOffset(magnitude: string): number {
    if (!magnitude) {
      return 60;
    }

    switch (magnitude) {
      case '1':
        return 72;
      case '2':
        return 70;
      case '3':
        return 68;
      case '4':
        return 65;
      case '5':
        return 60;
      case '6':
        return 58;
      case '7':
        return 55;
      default:
        return 60;
    }
  }
}
